import React from 'react';

import MemberCard, { IMemberCardConfig } from '../common/MemberCard';
import { IGroup, IGroupItems } from '../../types/customGroupingTypes';
import { formatCurrency } from '../../../../utils/tools/formatCurrenyToInteger';
import {
    GroupCSRLevels,
    PlAN_TYPES,
} from '../../../../constants/individualPortal/customGrouping/customGrouping.constants';
import { useAppDispatch, useModal, useStateCode } from '../../../../hooks';
import useCustomGrouping from '../../hooks/useCustomGrouping';

interface IUnenrolledGroupCardProps {
    group: IGroupItems;
    planType: PlAN_TYPES;
    unenrolledGroupSelected: Record<string, IGroup>;
    setUnenrolledGroupSelected: Function;
}

export default function UnenrolledGroupCard(props: IUnenrolledGroupCardProps) {
    const { group, planType, unenrolledGroupSelected, setUnenrolledGroupSelected } = props;
    
    const { openModal } = useModal();
    const dispatch = useAppDispatch();
    const { processGroupWarning } = useCustomGrouping({ dispatch });
    const { isNonStandardGroupFailureModalVisible } = useStateCode();

    const secondaryMembers: IMemberCardConfig[] = [];

    group.memberList.forEach(member => {
        if (
            (planType === PlAN_TYPES.HEALTH && member.healthEnrollmentId) ||
            (planType === PlAN_TYPES.DENTAL && member.dentalEnrollmentId)
        )
            return;
        secondaryMembers.push({
            data: member,
            config: {
                isCheckBoxVisible: true,
                isChecked: !!member.isChecked,
                isCheckBoxDisabled: !!member?.enrollmentId,
            },
        });
    });

    const onMemberChecked =
        ({ unenrolledGroupSelected }: { unenrolledGroupSelected: Record<string, IGroup> }) =>
        ({ secondaryMembers, setMembersState, isCheck = true }) => {
            const otherGroupSelected = Object.values(unenrolledGroupSelected).find(
                grp => grp?.id !== group.id && grp?.memberList?.length,
            );
            const updateGroupSelected = () => {
                setUnenrolledGroupSelected(prev => {
                    return {
                        ...prev,
                        [group.id]: {
                            ...group,
                            memberList: [
                                ...secondaryMembers
                                    .filter(member => member.config.isChecked)
                                    .map(member => ({
                                        ...member.data,
                                        isChecked: true,
                                    })),
                            ],
                        },
                    };
                });
            };

            //selectedGroup will contain the second instance of the group selected by user.
            let selectedGroup:IGroup;

            for (const grp of Object.values(unenrolledGroupSelected)) {
                if (!selectedGroup && grp?.id !== otherGroupSelected?.id) {
                    selectedGroup = grp?.id === group.id ? group : grp?.memberList?.length ? grp : selectedGroup;
                }
            }

            const { showMultiGroupFailureModal, modalProps = {} } = processGroupWarning(otherGroupSelected, selectedGroup!, isNonStandardGroupFailureModalVisible);
            if (showMultiGroupFailureModal && isCheck) {
                const onContinue = () => {
                    setMembersState(secondaryMembers);
                    updateGroupSelected();
                };

                openModal('failToFormMultiCustomGroup', { ...modalProps, onContinue });
                return false;
            }

            updateGroupSelected();
            return true;
        };

    function getPlanDetailsHeader() {
        const headers: {
            header: string;
            headerValues?: Record<string, any>;
        }[] = [];
        if (planType === PlAN_TYPES.HEALTH) {
            if (group.groupCsr !== GroupCSRLevels.CS1) {
                headers.push({
                    header: 'customGrouping.csrMsg',
                });
            }
        }

        if (group?.maxAptc >= 0) {
            headers.push({
                header: 'customGrouping.totalAPTC',
                headerValues: { amount: formatCurrency(group.maxAptc) },
            });
        }

        if (group?.maxStateSubsidy >= 0) {
            headers.push({
                header: 'customGrouping.totalStateSubsidy',
                headerValues: { amount: formatCurrency(group.maxStateSubsidy) },
            });
        }

        return headers;
    }

    const planDetails = {
        headers: getPlanDetailsHeader(),
        issuerName: group.issuerName,
        planName: group.planName,
        netPremium: group.netPremium || group.netPremium === 0 ? 'customGrouping.netPremium' : '',
        perimoumAmount: formatCurrency(group.netPremium),
    };

    return (
        <div className="mt-2">
            <div className="flex-container">
                <MemberCard
                    id={String(group.id)}
                    secondaryMembers={secondaryMembers}
                    planDetails={planDetails}
                    isBorderVisible
                    onMemberChecked={onMemberChecked({ unenrolledGroupSelected })}
                />
            </div>
        </div>
    );
}
